<app-simple-responsive-frame>
    <div class="form-container" [formGroup]="priceForm">
        <h2>
            ¿En cuánto ofreces tu {{ segmentDisplay!.toLowerCase() }} para {{ offerDisplay!.toLowerCase() }}?
        </h2>
        <p class="description">
            Escribe el precio en {{ currencyName }}. <b>Define tu precio con cuidado</b>; para modificarlo
            tendrás que contactarnos. Esto ayuda a mantener compromiso y transparencia en tu anuncio.
        </p>

        <!-- Anticrético: precio único + duración -->
        <div class="price-row" [class.with-period]="isRentOrLeasing" *ngIf="!hasBreakdown">
            <div class="price-field">
                <div class="input-wrap" [class.invalid]="priceInvalid">
                    <span class="prefix">{{ currencyPrefix }}</span>
                    <input
                        class="price-input"
                        type="text"
                        inputmode="decimal"
                        autocomplete="off"
                        formControlName="price"
                        [placeholder]="pricePlaceholder"
                        aria-label="Precio"
                        (input)="onPriceTyping($event)"
                        (focus)="unformatPrice()"
                        (blur)="formatPrice()"
                    />
                    <button type="button" class="help" aria-label="Ayuda sobre precio" (click)="openPriceHelp()">
                        <i class="pi pi-question-circle"></i>
                    </button>
                </div>
                <div class="error" *ngIf="priceInvalid">Ingresa un precio válido.</div>
            </div>

            <div class="period-field" *ngIf="isRentOrLeasing">
                <select formControlName="period" aria-label="Frecuencia de pago" class="period-select">
                    <option value="" disabled selected>Selecciona frecuencia</option>
                    <option *ngFor="let option of periodOptions" [value]="option.optionCode">
                        {{ option.optionName }}
                    </option>
                </select>
                <div class="error" *ngIf="needPeriod && !priceForm.get('period')?.value">
                    Selecciona una frecuencia.
                </div>
            </div>
        </div>

        <!-- Venta/Alquiler: desglose por componente (construcción + garage/baulera con cantidad) -->
        <div class="sale-breakdown" *ngIf="hasBreakdown && saleForm" [formGroup]="saleForm">
            <div class="components">
                <ng-container *ngFor="let c of saleComponents; let i = index">
                    <span class="plus" *ngIf="i > 0" aria-hidden="true">+</span>
                    <div class="component">
                        <label class="component-label">{{ c.label }}</label>
                        <div class="input-wrap">
                            <span class="prefix">{{ currencyPrefix }}</span>
                            <input
                                class="price-input"
                                type="text"
                                inputmode="decimal"
                                autocomplete="off"
                                [formControlName]="'price__' + c.code"
                                [placeholder]="pricePlaceholder"
                                [attr.aria-label]="c.label"
                                (input)="onSalePriceInput($event, c)"
                                (focus)="onSalePriceFocus(c)"
                                (blur)="onSalePriceBlur(c)"
                            />
                            <button type="button" class="help" *ngIf="c.isMain" aria-label="Ayuda sobre precio" (click)="openPriceHelp()">
                                <i class="pi pi-question-circle"></i>
                            </button>
                        </div>

                        <div class="counter" *ngIf="c.hasCount">
                            <span class="counter-label">{{ c.countLabel }}</span>
                            <button type="button" class="step" (click)="decCount(c)" aria-label="Quitar uno">
                                <i class="pi pi-minus-circle"></i>
                            </button>
                            <span class="count">{{ countOf(c) }}</span>
                            <button type="button" class="step" (click)="incCount(c)" aria-label="Agregar uno">
                                <i class="pi pi-plus-circle"></i>
                            </button>
                        </div>
                    </div>
                </ng-container>
            </div>
        </div>

        <!-- Alquiler: temporalidad (cada cuánto se paga). El control 'period' vive en priceForm. -->
        <div class="rent-period" *ngIf="isRent && saleForm">
            <label class="rent-period__label">¿Cada cuánto se paga el alquiler?</label>
            <select formControlName="period" class="period-select" aria-label="Temporalidad del alquiler">
                <option value="" disabled selected>Selecciona la temporalidad</option>
                <option *ngFor="let option of periodOptions" [value]="option.optionCode">
                    {{ option.optionName }}
                </option>
            </select>
        </div>

        <p class="sale-total" *ngIf="hasBreakdown && saleForm">
            El precio total de tu {{ segmentDisplay }} es de:
            <b>{{ currencyPrefix }} {{ saleTotalDisplay }}</b>
        </p>

        <p class="zone-hint" *ngIf="!zonePriceUnavailable">
            En la zona de tu {{ segmentDisplay }}, el metro cuadrado (m²) está
            en {{ zonePriceDisplay }} {{ currencyPrefix }}.
        </p>
        <p class="zone-hint zone-hint--muted" *ngIf="zonePriceUnavailable">
            Aún no tenemos un precio de referencia para tu zona.
        </p>

        <button type="button" class="compare-btn" (click)="openZoneMap()">
            Compara precios de {{ offerDisplay!.toLowerCase() }} en tu zona
            <i class="pi pi-plus"></i>
        </button>
        <br>
        <a class="policies" (click)="onPolicies()">Lee las políticas sobre precio</a>
    </div>

    <app-page-navigation-bar
        [value]="95"
        [type]="'complete'"
        [previousPageURL]="previousUrl"
        [nextPageURL]="nextUrl"
        [flowMode]="currentMode"
        [stepKey]="'price'"
        (goNext)="handleNextStep()"
        (goPrevious)="handlePreviousStep()"
        (saveAndExit)="handleSaveAndExit()">
    </app-page-navigation-bar>
    <app-zone-price-map-dialog
        #priceDialog
        [cityId]="selectedCityId"
        [segmentId]="selectedSegmentId"
        [adId]="selectedAdId"
        [zoneId]="selectedZoneId"
        [segmentLabel]="segmentDisplay"
        [offerLabel]="offerDisplay">
    </app-zone-price-map-dialog>

    <!-- Popup de ayuda "?" — Información sobre el precio (ref. Figma) -->
    <p-dialog
        [(visible)]="showPriceHelp"
        [modal]="true"
        [dismissableMask]="true"
        [draggable]="false"
        [resizable]="false"
        [showHeader]="false"
        [style]="{width:'min(92vw, 460px)'}"
        [contentStyle]="{padding:'0'}"
        styleClass="price-help-dialog">
        <div class="price-help">
            <button type="button" class="price-help__close" aria-label="Cerrar" (click)="showPriceHelp=false">
                <i class="pi pi-times-circle"></i>
            </button>

            <h3 class="price-help__title">Información sobre el precio</h3>
            <p class="price-help__intro">
                Tú defines el precio de tu propiedad. Para modificarlo más adelante tendrás que
                contactarnos; esto ayuda a mantener el compromiso y la transparencia de tu anuncio.
            </p>

            <h4 class="price-help__h">Precio sugerido</h4>
            <p>
                El precio sugerido se basa en factores como la ubicación de tu propiedad, sus
                características y la demanda, además de los precios de propiedades similares en tu zona.
            </p>

            <h4 class="price-help__h">Comparar propiedades similares</h4>
            <p>
                Para determinar las propiedades similares a la tuya consideramos criterios como la
                ubicación, el tipo de propiedad, los ambientes, las características y las propiedades que
                los usuarios suelen ver junto a la tuya. Evitamos incluir anuncios poco activos —por
                ejemplo, los que no se han publicado o actualizado recientemente—, de modo que obtengas
                una referencia realista del precio por m² en tu zona.
            </p>
        </div>
    </p-dialog>
</app-simple-responsive-frame>
